/** Kaçış drawing helpers (Phaser Graphics only; no game rules here). */
import Phaser from "phaser";

export interface Pt {
  x: number;
  y: number;
}

export function lighten(c: number, t: number): number {
  const r = (c >> 16) & 255;
  const g = (c >> 8) & 255;
  const b = c & 255;
  const f = (v: number) => Math.round(v + (255 - v) * t);
  return (f(r) << 16) | (f(g) << 8) | f(b);
}

export function darken(c: number, t: number): number {
  const r = (c >> 16) & 255;
  const g = (c >> 8) & 255;
  const b = c & 255;
  const f = (v: number) => Math.round(v * (1 - t));
  return (f(r) << 16) | (f(g) << 8) | f(b);
}

export interface TubeOpts {
  /** body radius */
  r: number;
  color: number;
  /** radius multiplier by position (exit shrink); <= 0 hides the sample */
  scaleAt?: (p: Pt) => number;
  /** extra white glow while grabbed */
  glow?: boolean;
  /** draw eyes at the polyline start (head) */
  eyes?: boolean;
  /** 1 = full body, used for the breathing squash */
  squash?: number;
}

/** Jelly tube along a head→tail polyline: dark outline, body, gloss, tapered tail, eyes at the head. */
export function drawTube(g: Phaser.GameObjects.Graphics, poly: Pt[], o: TubeOpts): void {
  if (poly.length < 2) return;
  const cum: number[] = [0];
  for (let i = 1; i < poly.length; i++) cum.push(cum[i - 1]! + Math.hypot(poly[i]!.x - poly[i - 1]!.x, poly[i]!.y - poly[i - 1]!.y));
  const total = Math.max(1, cum[cum.length - 1]!);
  const step = o.r * 0.5;
  const N = Math.max(2, Math.ceil(total / step));
  const taper = o.r * 2.4;
  const xs: number[] = [];
  const ys: number[] = [];
  const rs: number[] = [];
  let seg = 1;
  for (let i = 0; i <= N; i++) {
    const d = (total * i) / N;
    while (seg < cum.length - 1 && cum[seg]! < d) seg++;
    const a = poly[seg - 1]!;
    const b = poly[seg]!;
    const span = cum[seg]! - cum[seg - 1]!;
    const t = span > 0 ? (d - cum[seg - 1]!) / span : 0;
    const p = { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
    const fromTail = total - d;
    let rr = o.r * (fromTail < taper ? 0.64 + 0.36 * (fromTail / taper) : 1) * (o.squash ?? 1);
    if (o.scaleAt) rr *= Math.max(0, o.scaleAt(p));
    xs.push(p.x);
    ys.push(p.y);
    rs.push(rr);
  }
  const edge = Math.max(2.5, o.r * 0.1);
  if (o.glow) {
    g.fillStyle(0xffffff, 0.2);
    for (let i = 0; i <= N; i++) if (rs[i]! > 1.5) g.fillCircle(xs[i]!, ys[i]!, rs[i]! + edge + 6);
  }
  g.fillStyle(darken(o.color, 0.5), 1);
  for (let i = 0; i <= N; i++) if (rs[i]! > 1.5) g.fillCircle(xs[i]!, ys[i]!, rs[i]! + edge);
  g.fillStyle(o.color, 1);
  for (let i = 0; i <= N; i++) if (rs[i]! > 1.5) g.fillCircle(xs[i]!, ys[i]!, rs[i]!);
  g.fillStyle(darken(o.color, 0.18), 0.55);
  for (let i = 0; i <= N; i += 2) if (rs[i]! > 4) g.fillCircle(xs[i]! + rs[i]! * 0.08, ys[i]! + rs[i]! * 0.34, rs[i]! * 0.6);
  g.fillStyle(lighten(o.color, 0.55), 0.5);
  for (let i = 0; i <= N; i += 2) if (rs[i]! > 4) g.fillCircle(xs[i]! - rs[i]! * 0.2, ys[i]! - rs[i]! * 0.3, rs[i]! * 0.34);
  if (o.eyes && rs[0]! > 6) {
    const h = poly[0]!;
    const n = poly[1]!;
    let fx = h.x - n.x;
    let fy = h.y - n.y;
    const fl = Math.hypot(fx, fy) || 1;
    fx /= fl;
    fy /= fl;
    const px = -fy;
    const py = fx;
    const R = rs[0]!;
    for (const side of [-1, 1]) {
      const ex = h.x + fx * R * 0.18 + px * side * R * 0.46;
      const ey = h.y + fy * R * 0.18 + py * side * R * 0.46;
      g.fillStyle(0xffffff, 1).fillCircle(ex, ey, R * 0.33);
      g.fillStyle(0x1b1f3a, 1).fillCircle(ex + fx * R * 0.1, ey + fy * R * 0.1, R * 0.17);
      g.fillStyle(0xffffff, 1).fillCircle(ex + fx * R * 0.1 - R * 0.05, ey + fy * R * 0.1 - R * 0.06, R * 0.06);
    }
  }
}

/** A gate arch drawn in local space where +Y points outward from the board; rotate the container to place it. */
export function drawGateShape(g: Phaser.GameObjects.Graphics, cell: number, color: number): void {
  const w = cell * 0.84;
  const d = cell * 0.44;
  const rad = d * 0.85;
  g.fillStyle(darken(color, 0.45), 1).fillRoundedRect(-w / 2 - 3, -3, w + 6, d + 6, { tl: 4, tr: 4, bl: rad + 3, br: rad + 3 });
  g.fillStyle(color, 1).fillRoundedRect(-w / 2, -1, w, d, { tl: 3, tr: 3, bl: rad, br: rad });
  g.fillStyle(lighten(color, 0.45), 0.5).fillRoundedRect(-w / 2 + 4, 1, w - 8, d * 0.28, { tl: 2, tr: 2, bl: 8, br: 8 });
  const iw = w * 0.62;
  const idp = d * 0.78;
  g.fillStyle(0x0b0d1c, 1).fillRoundedRect(-iw / 2, -2, iw, idp, { tl: 2, tr: 2, bl: idp * 0.7, br: idp * 0.7 });
  g.fillStyle(lighten(color, 0.3), 0.85);
  const cy = idp * 0.42;
  g.fillTriangle(-iw * 0.2, cy - 5, iw * 0.2, cy - 5, 0, cy + 6);
}

export function drawWallShape(g: Phaser.GameObjects.Graphics, size: number): void {
  const s = size * 0.9;
  g.fillStyle(0x2d3157, 1).fillRoundedRect(-s / 2, -s / 2 + 4, s, s, 14);
  g.fillStyle(0x59608f, 1).fillRoundedRect(-s / 2, -s / 2, s, s - 4, 14);
  g.fillStyle(0x7b83b8, 0.55).fillRoundedRect(-s / 2 + 5, -s / 2 + 4, s - 10, s * 0.28, 10);
  g.lineStyle(2, 0x3c4272, 0.9);
  g.lineBetween(-s * 0.2, -s * 0.05, s * 0.05, s * 0.2);
  g.lineBetween(s * 0.05, s * 0.2, s * 0.25, s * 0.12);
}

export function drawStoneShape(g: Phaser.GameObjects.Graphics, size: number): void {
  const s = size * 0.9;
  g.fillStyle(0x5a3d2b, 1).fillRoundedRect(-s / 2, -s / 2 + 4, s, s, 20);
  g.fillStyle(0xa8764f, 1).fillRoundedRect(-s / 2, -s / 2, s, s - 4, 20);
  g.fillStyle(0xd4a276, 0.5).fillRoundedRect(-s / 2 + 6, -s / 2 + 4, s - 12, s * 0.26, 12);
  g.lineStyle(3, 0x6e4a33, 0.9);
  g.lineBetween(-s * 0.32, s * 0.18, -s * 0.12, s * 0.02);
  g.lineBetween(s * 0.3, -s * 0.2, s * 0.14, -s * 0.06);
}
